import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { ALL_SERVICES } from "@/lib/services";

const EMPTY = { name: "", phone: "", email: "", message: "" };

const validate = (f) => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Please share your full name.";
    if (!/^[+]?[0-9\s-]{7,15}$/.test(f.phone.trim())) e.phone = "Enter a valid mobile number.";
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Enter a valid email address.";
    return e;
};

// One enquiry form for every service — posts to the shared /api/leads with source "service_enquiry".
export function ServiceEnquiryForm({ service, dark = false, testid = "service-enquiry-form" }) {
    const [f, setF] = useState({ ...EMPTY, service: service || ALL_SERVICES[0].t });
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const upd = (k, v) => { setF((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate(f);
        setErrors(e);
        if (Object.values(e).some(Boolean)) { toast.error("Please fix the highlighted fields."); return; }
        setBusy(true);
        try {
            await api.post("/leads", {
                name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim() || null,
                service: f.service, source: "service_enquiry",
                message: `[${f.service}] ${f.message.trim()}`.trim(),
            });
            toast.success("Thank you. An EBG advisor will call you shortly.");
            setDone(true);
            setF({ ...EMPTY, service: f.service });
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const box = dark ? "bg-white/[0.04] border-white/15 text-white placeholder:text-white/40" : "bg-white border-black/15 text-black";
    const input = `w-full border px-3 py-3 text-sm outline-none focus:border-[var(--ebg-gold)] ${box}`;
    const err = (k) => errors[k] && <div className="text-[11px] text-red-500 mt-1" data-testid={`${testid}-error-${k}`}>{errors[k]}</div>;

    if (done) {
        return (
            <div className={`p-6 text-center border ${dark ? "border-white/15" : "border-black/10 bg-white"}`} data-testid={`${testid}-success`}>
                <CheckCircle2 size={28} className="mx-auto text-[var(--ebg-gold)]"/>
                <div className="font-serif text-2xl mt-3">Enquiry received</div>
                <p className={`text-[13px] mt-2 ${dark ? "text-white/60" : "text-black/60"}`}>Our {f.service} team will contact you shortly.</p>
                <button type="button" onClick={() => setDone(false)} data-testid={`${testid}-again`} className="mt-4 text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)]">Send another enquiry</button>
            </div>
        );
    }
    return (
        <form onSubmit={submit} noValidate className="space-y-3 text-left" data-testid={testid}>
            <div><input placeholder="Full name *" value={f.name} onChange={(e) => upd("name", e.target.value)} className={input} data-testid={`${testid}-name`}/>{err("name")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div><input placeholder="Mobile *" inputMode="tel" value={f.phone} onChange={(e) => upd("phone", e.target.value)} className={input} data-testid={`${testid}-phone`}/>{err("phone")}</div>
                <div><input placeholder="Email (optional)" type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} className={input} data-testid={`${testid}-email`}/>{err("email")}</div>
            </div>
            <select value={f.service} onChange={(e) => upd("service", e.target.value)} className={input} data-testid={`${testid}-service`}>
                {ALL_SERVICES.map((s) => <option key={s.slug} value={s.t} className="text-black">{s.t}</option>)}
            </select>
            <textarea rows={3} placeholder="Tell us about your requirement" value={f.message} onChange={(e) => upd("message", e.target.value)} className={input} data-testid={`${testid}-message`}/>
            <button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="w-full bg-[var(--ebg-gold)] text-black py-3.5 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={13} className="animate-spin"/>} {busy ? "Sending…" : "Submit Enquiry"}
            </button>
        </form>
    );
}

export function ServiceEnquiryDialog({ service, open, onOpenChange }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg rounded-none" data-testid="service-enquiry-dialog">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Enquire Now</div>
                <DialogTitle className="font-serif text-2xl font-normal">{service || "Our Services"}</DialogTitle>
                <DialogDescription className="text-[13px] text-black/60 -mt-1">Share your details and an EBG advisor will call you back.</DialogDescription>
                {open && <ServiceEnquiryForm key={service} service={service} testid="dialog-enquiry-form"/>}
            </DialogContent>
        </Dialog>
    );
}
